import { MagicBackground, PersistentStore } from "@/stores/persistent_store";
import { computed, Ref, ref } from "vue";
import { useEditorStore } from "@/stores/editor_store";
import { useMonitorSize } from "@/composables/monitor_size";
import Client from "@/modules/internal_api/client";
import { ProcessingState, Image } from "@/modules/internal_api/image";

export enum GenerateState {
  Initial = "initial", // entry/generate state
  Loading = "loading",
  Error = "error",
  Generated = "generated", // generate again state
}

export function useMagicBackground(persistentStore: Ref<PersistentStore>) {
  const currentState = ref<GenerateState>(GenerateState.Initial);

  // TODO: When integating api this has to be based on the response from polling endpoint, this should reflect ProcessingState.Processing from MB response
  const isLoading = computed(() => currentState.value === GenerateState.Loading);
  // TODO: This error property should be based on the response from polling endpoint
  // additionally it can be re-set over time - since this property is only used in the view it doesn't have to be reflect current state of the MB response
  const isError = computed(() => currentState.value === GenerateState.Error);
  const isGenerated = computed(
    () =>
      persistentStore.value.selectedMagicBackground !== null &&
      persistentStore.value.selectedMagicBackground.state === ProcessingState.Finished &&
      persistentStore.value.selectedMagicBackgroundTemplate === null
  );

  const isInitial = computed(() => {
    const isStateInitial = currentState.value === GenerateState.Initial;
    const hasSelectedTemplate = persistentStore.value.selectedMagicBackgroundTemplate !== null;
    const hasNoGeneratedBackgrounds = editorStore.selectedImage?.magicBackgroundResults.size === 0;
    console.log(1, isStateInitial, hasNoGeneratedBackgrounds, hasSelectedTemplate);

    return isStateInitial || hasNoGeneratedBackgrounds || hasSelectedTemplate;
  });

  const { isMobile, isMobileLarge } = useMonitorSize();
  const editorStore = useEditorStore();

  const generateBackground = async (templateId: string) => {
    if (isLoading.value) return;
    editorStore.isApplyingBackgroundChanges = true;

    currentState.value = GenerateState.Loading;

    const image = await Client.mockGenerateBackground(editorStore.selectedImage, templateId);

    if (image) {
      editorStore.updateImage(image);
      const parsedBackgrounds = parseBackgrounds(image);
      console.log("parsedBackgrounds", parsedBackgrounds);
      console.log({ image });
      if (
        parsedBackgrounds.length > 0 &&
        parsedBackgrounds[parsedBackgrounds.length - 1].state === ProcessingState.Finished
      ) {
        persistentStore.value.setMagicBackgrounds(parseBackgrounds(image));
        persistentStore.value.selectMagicBackground(parsedBackgrounds[parsedBackgrounds.length - 1]);

        currentState.value = GenerateState.Generated;
      } else if (parsedBackgrounds[parsedBackgrounds.length - 1].state === ProcessingState.Error) {
        currentState.value = GenerateState.Error;

        setTimeout(() => {
          currentState.value = GenerateState.Initial;
        }, 2000);
      }
      editorStore.isApplyingBackgroundChanges = false;
    }
  };

  const resetError = () => {
    if (currentState.value === GenerateState.Error) {
      currentState.value = GenerateState.Initial;
    }
  };

  const isGenerateMode = computed(() => {
    const shouldShowGenerateMode =
      persistentStore.value.selectedMagicBackgroundTemplate !== null ||
      (persistentStore.value.selectedMagicBackground !== null && persistentStore.value.magicBackgrounds.length > 0);

    console.log({
      shouldShowGenerateMode,
      selectedMagicBackgroundTemplate: persistentStore.value.selectedMagicBackgroundTemplate,
      selectedMagicBackground: persistentStore.value.selectedMagicBackground,
      magicBackgroundsLength: persistentStore.value.magicBackgrounds.length,
    });

    if (isMobile.value || isMobileLarge.value) {
      return shouldShowGenerateMode && editorStore.openedPanel === "addBackground";
    }
    return shouldShowGenerateMode;
  });

  const parseBackgrounds = (image: Image): MagicBackground[] => {
    const generatedBackgrounds = image.magicBackgroundResults;
    const parsedBackgrounds = [];

    generatedBackgrounds.forEach((result, id) => {
      if ("url" in result.magicBackgroundPreviewResult) {
        parsedBackgrounds.push({
          id,
          state: result.magicBackgroundPreviewResult.state,
          url: result.magicBackgroundPreviewResult.url,
          templateId: result.magicBackgroundPreviewResult.templateId,
        });
      }
    });

    return parsedBackgrounds;
  };

  return {
    // States
    currentState,
    isLoading,
    isError,
    isGenerated,
    isInitial,

    // Actions
    generateBackground,
    resetError,
    isGenerateMode,
    parseBackgrounds,
  };
}
